import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useFetch } from 'use-http';
import { useController, useFormState, useWatch } from 'react-hook-form';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { ApiRoutes } from 'constants/api';
import { AVAILABLE_DAY_DATE_FORMAT, DATE_FORMAT } from 'constants/time';
import { availableDatesFrom } from 'utils/availableDates';
import { dateFormatter, isDateDeliveryChecker } from 'utils/dateHelpers';
import { PurchaseFormFieldNames as FIELD_NAMES } from 'constants/form';
import { GLOBAL_EVENTS } from 'events/constants';
import {
  getDefaultDatePickerDate,
  getNextAvailableDeliveryDate,
  getPreselectedMonthQuery,
} from './helpers/getNextDeliveryDate';
import {
  AvailableDate,
  DateChangeEventPayload,
  GetAvailableDatesResponse,
  MaxDateOffset,
  UseDeliveryDatesProps,
} from './interface';

const dispatchDateChangeEvent = (
  date: string,
  shippingRouteId: number | null,
) => {
  const event = new CustomEvent<DateChangeEventPayload>(
    GLOBAL_EVENTS.product.deliveryDateChange,
    {
      detail: { date, quantity: 1, shipping_route_id: shippingRouteId },
    },
  );
  document.dispatchEvent(event);
};

const DEFAULT_MAX_DATE = dayjs().add(6, 'month');

function getMaxDate(maxDateOffset: MaxDateOffset | undefined) {
  if (maxDateOffset) {
    return dayjs().add(maxDateOffset.amount, maxDateOffset.unit);
  }

  return DEFAULT_MAX_DATE;
}

export const useDeliveryDates = ({
  control,
  fieldName,
  productId,
  purchasable,
  setShippingOptions,
}: UseDeliveryDatesProps) => {
  const [initialDate, setInitialDateState] = useState<Dayjs | null>(null);
  const [modalOpen, toggleModal] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const {
    get: getAvailability,
    abort: availabilityAbort,
    loading: availabilityLoading,
    response: availabilityResponse,
    data: availableDatesData,
  } = useFetch<GetAvailableDatesResponse>();

  const availableDates = useMemo(
    () => availableDatesFrom<AvailableDate>(availableDatesData),
    [availableDatesData],
  );

  const availableDateMap = useMemo(
    () =>
      availableDates.reduce(
        (acc, dateObj) => {
          acc[dateObj.date] = dateObj;
          return acc;
        },
        {} as Record<string, (typeof availableDates)[number]>,
      ),
    [availableDates],
  );

  const { errors } = useFormState({ control });
  const formDate = useWatch({ control, name: fieldName });
  const zipCode = useWatch({ control, name: FIELD_NAMES.zipCode });
  const deliveryDate = useWatch({ control, name: FIELD_NAMES.date });
  const filterDate = useWatch({ control, name: FIELD_NAMES.filterDate });
  const isDateDelivery = isDateDeliveryChecker(dayjs(dateFormatter(formDate)));

  const {
    field: { name, onChange },
  } = useController({ name: fieldName, control });
  const {
    field: { value: noStock, onChange: setNoStock },
  } = useController({ name: FIELD_NAMES.noStock, control });
  const {
    field: { value: shippingMethodType, onChange: setShippingMethodType },
  } = useController({ name: FIELD_NAMES.shippingMethodType, control });
  const {
    field: { onChange: setShippingRouteId },
  } = useController({ name: FIELD_NAMES.shippingRouteId, control });
  const {
    field: { value: dateInvalid, onChange: setDateInvalid },
  } = useController({ name: FIELD_NAMES.dateInvalid, control });

  const setFormDateCb = useCallback(
    (date: Dayjs | null) => {
      onChange(date?.format(DATE_FORMAT));
      setInitialDateState(date);
    },
    [onChange],
  );

  const shouldDisableDate = useCallback(
    (date_: MaterialUiPickersDate) => {
      const SUNDAY = 0;
      if (!availabilityResponse.ok || !date_) return false;
      if (date_.day() === SUNDAY) return true;

      return !availableDates
        .map((obj) => obj.date)
        .includes(date_?.format(AVAILABLE_DAY_DATE_FORMAT));
    },
    [availableDates],
  );

  // Validate a typed/picked date against the same rules the calendar enforces.
  // Returns an accessible error message, or null when the date can be used.
  const getValidationError = useCallback(
    (date: Dayjs): string | null => {
      if (date.isBefore(dayjs(), 'day')) {
        return 'Delivery date cannot be in the past.';
      }
      if (shouldDisableDate(date)) {
        const nextAvailable = getNextAvailableDeliveryDate(
          date,
          availableDates,
        );
        return nextAvailable
          ? `This date is unavailable. The next available date is ${nextAvailable.format(
              'MMMM D, YYYY',
            )}.`
          : 'This date is unavailable.';
      }
      return null;
    },
    [shouldDisableDate, availableDates],
  );

  /**
   * We only toggle a modal on Date accept (i.e. when user selects a date),
   * not when date changes programmatically. Past/unavailable/invalid dates are
   * rejected with an accessible message instead of being committed.
   * */
  const handleDateAccept = useCallback(
    (date: MaterialUiPickersDate) => {
      if (!date || !date.isValid()) return;

      const message = getValidationError(date);
      if (message) {
        setValidationError(message);
        return;
      }

      setValidationError(null);
      if (isDateDeliveryChecker(date)) {
        setFormDateCb(date);
      } else {
        toggleModal(true);
      }
    },
    [getValidationError, setFormDateCb, toggleModal],
  );

  const handleDateChange = useCallback(
    (date: MaterialUiPickersDate) => {
      // While typing, only reflect the value in the field. Validation and the
      // commit happen on blur (typed input) or on accept (calendar day click),
      // never on every keystroke.
      if (!date) return;
      setInitialDateState(date);
    },
    [setInitialDateState],
  );

  // On focus-out, validate the typed date and, if usable, run exactly what a
  // calendar day click does (commit + downstream addon/price reload). Skipped
  // when the value is unchanged (e.g. blur caused by opening the calendar) or
  // incomplete, so we don't clobber the current selection.
  const handleDateBlur = useCallback(() => {
    if (!initialDate || !initialDate.isValid()) return;
    if (initialDate.format(DATE_FORMAT) === formDate) return;
    handleDateAccept(initialDate);
  }, [initialDate, formDate, handleDateAccept]);

  const handleMonthChange = useCallback(
    (date: MaterialUiPickersDate) => {
      if (!date || !zipCode || !purchasable) return undefined;
      availabilityAbort();

      return new Promise<void>((resolve) => {
        getAvailability(
          `/products/${productId}${
            ApiRoutes.AvailabilityWithPrices
          }?quantity=1&zip_code=${zipCode}&month=${date.month() + 1}&year=${date.year()}`,
        ).then(() => {
          resolve();
        });
      });
    },
    [zipCode, purchasable, availabilityAbort, getAvailability, productId],
  );

  const initiallyHighlightedDate = useMemo(() => {
    if (!availabilityResponse.ok) return null;

    const highlightDate = getDefaultDatePickerDate(
      formDate || filterDate,
      availableDates,
    );

    if (noStock !== !highlightDate) setNoStock(!highlightDate);

    return highlightDate || null;
  }, [availableDates]);

  const getHelperText = useCallback(() => {
    if (!zipCode) return 'Please select a valid zip code first';
    if (availabilityResponse.status >= 400)
      return 'There was an error, please try again later';
    if (validationError) return validationError;

    return errors[fieldName]?.message;
  }, [
    zipCode,
    availabilityResponse.status,
    validationError,
    errors[fieldName],
  ]);

  // Mirror the validation state into the form so the Add To Cart button can
  // disable while the entered date is unusable. Only the main delivery-date
  // picker drives the flag (the subscription pickers share this hook).
  useEffect(() => {
    if (fieldName !== FIELD_NAMES.date) return;
    const invalid = Boolean(validationError);
    if (dateInvalid !== invalid) setDateInvalid(invalid);
  }, [fieldName, validationError, dateInvalid, setDateInvalid]);

  useEffect(() => {
    if (zipCode && purchasable) {
      availabilityAbort();
      setShippingRouteId(null);
      window.showAddonsSkeleton?.();
      getAvailability(
        `/products/${productId}${
          ApiRoutes.AvailabilityWithPrices
        }?quantity=1&zip_code=${zipCode}${getPreselectedMonthQuery(
          formDate || filterDate,
        )}`,
      );
    }
  }, [zipCode]);

  useEffect(() => {
    if (availableDates?.length && initiallyHighlightedDate) {
      setFormDateCb(initiallyHighlightedDate);
    }
  }, [JSON.stringify(availableDates)]);

  useEffect(() => {
    if (
      !availabilityLoading &&
      availabilityResponse.ok &&
      availableDates.length === 0
    ) {
      window.clearAddonsSkeleton?.();
    }
  }, [availabilityLoading, availabilityResponse.ok, availableDates.length]);

  useEffect(() => {
    if (fieldName === FIELD_NAMES.startDate) {
      setFormDateCb(dayjs(dateFormatter(deliveryDate)));
    }
  }, [deliveryDate]);

  useEffect(() => {
    if (fieldName === FIELD_NAMES.date && deliveryDate && setShippingOptions) {
      const formattedDate = dayjs(dateFormatter(deliveryDate)).format(
        AVAILABLE_DAY_DATE_FORMAT,
      );
      const shippingRouteId =
        availableDateMap[formattedDate]?.shipping_route_id ?? null;
      setShippingRouteId(shippingRouteId);
      const shippingOptions =
        availableDateMap[formattedDate]?.shipping_method_types ?? [];
      setShippingOptions(shippingOptions);
      const selectedShippingMethod = shippingOptions.find(
        (method) => method.id === shippingMethodType,
      );

      if (!selectedShippingMethod) {
        const fallbackId = shippingOptions.length ? shippingOptions[0].id : '';
        if (shippingMethodType !== fallbackId) {
          setShippingMethodType(fallbackId);
        }
      }

      if (shippingRouteId) {
        dispatchDateChangeEvent(deliveryDate, shippingRouteId);
      }
    }
  }, [deliveryDate, availableDateMap]);

  return {
    name,
    modalOpen,
    getMaxDate,
    toggleModal,
    initialDate,
    setFormDateCb,
    getHelperText,
    availableDateMap,
    handleDateChange,
    handleDateAccept,
    handleDateBlur,
    handleMonthChange,
    shouldDisableDate,
    initiallyHighlightedDate,
    error: fieldName in errors || Boolean(validationError),
    disabled:
      noStock ||
      !purchasable ||
      availabilityLoading ||
      !availabilityResponse.ok,
    label: isDateDelivery ? 'Delivery Date' : 'Delivery Window',
  };
};
